import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';

import { useAuth } from '../components/AuthContext';
import { ErrorMessageSnackbar } from '../components/ErrorMessageSnackbar';
import theme from '../theme';

const AuthPage = () => {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { authError, clearAuthError } = useAuth();

  const auth = getAuth();

  const handleLogin = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setLoading(true);

    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        setLoading(false);
        // Redirect to the intended path or default to home
        // Priority: 1. URL redirect parameter, 2. location.state.from, 3. default home
        const urlParams = new URLSearchParams(location.search);
        const redirectParam = urlParams.get('redirect');

        // Normaliser le chemin de redirection
        let redirectTo = redirectParam || location.state?.from || '/';

        // S'assurer que le chemin commence par /
        if (redirectTo && !redirectTo.startsWith('/')) {
          redirectTo = '/' + redirectTo;
        }

        // Nettoyer l'URL en supprimant le paramètre redirect
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('redirect');
        window.history.replaceState({}, '', newUrl.toString());

        navigate(redirectTo);
      })
      .catch((error) => {
        setLoading(false);
        setErrorMessage(`${t(error.code)}`);
      });
  };

  return (
    <>
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: theme.palette.saffron.main }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            {t('Sign in')}
          </Typography>
          <Box component="form" noValidate onSubmit={handleLogin} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              className="inputElement"
              required
              fullWidth
              id="email"
              label={t('Email address')}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              className="inputElement"
              name="password"
              label={t('Password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <FormControlLabel
              className="inputElement"
              control={<Checkbox value="remember" color="primary" />}
              label={t('Remember me')}
            />
            <Button
              fullWidth
              sx={{
                marginTop: 1,
                marginBottom: 2,
                fontSize: '18px !important',
              }}
              className="ActionButton"
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('Sign in')}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link
                  href="/password-reset"
                  variant="body2"
                  sx={{ textDecorationColor: 'inherit', color: theme.palette.saffron.main }}
                >
                  {t('Forgot password?')}
                </Link>
              </Grid>
              <Grid item>
                <Link
                  href="https://form.typeform.com/to/nWnjtS4a"
                  target="_blank"
                  variant="body2"
                  sx={{ textDecorationColor: 'inherit', color: theme.palette.saffron.main }}
                >
                  {t("Don't have an account? Sign up")}
                </Link>
              </Grid>
            </Grid>
            {errorMessage && (
              <Typography sx={{ marginTop: 2, color: 'red !important' }} variant="body1" align="center">
                {errorMessage}
              </Typography>
            )}
          </Box>
        </Box>
      </Container>
      <ErrorMessageSnackbar
        open={!!authError}
        message={authError || ''}
        handleClose={clearAuthError}
        severity="error"
      />
    </>
  );
};

export default AuthPage;
